import { A } from '@ember/array';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type { Owner } from '@ember/test-helpers/build-owner';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { differenceInDays } from 'date-fns';
import { task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import type DraftModel from 'garaje/models/draft';
import type EmployeeModel from 'garaje/models/employee';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { DRAFT_SHARE_OPTIONS, DraftSharingStrategy, LIVE_MAP_SHARE_INCLUDE_FIELDS } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';

type SelectOptionType = {
  label: string;
  value: string;
};

interface ShareDraftModalArgs {
  draft: DraftModel;
}

interface mapSharingItem {
  shareId: string;
  url: string;
  config: {
    include: string[];
  };
  expiresAt: string | null;
}

export default class ShareDraftModal extends Component<ShareDraftModalArgs> {
  @service declare store: Store;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare ajax: AjaxService;

  @(employeesSearcherTask({
    filter: { deleted: false },
    prefix: true,
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  @tracked selectedEmployees: EmployeeModel[] = [];
  @tracked sendEmailNotification: boolean = true;
  @tracked emailMessage: string = '';
  @tracked isSharingWithEveryone: boolean = false;
  @tracked externalSharingInfo: mapSharingItem | undefined;
  @tracked shouldHideSensitiveInfo: boolean = true;
  @tracked shouldShowConfirmationModal: boolean = false;
  @tracked pendingShareStrategy: SelectOptionType | undefined;
  @tracked isConfirmingSensitiveInfoChange: boolean = false;

  constructor(owner: Owner, args: ShareDraftModalArgs) {
    super(owner, args);
    void this.initializeDataTask.perform();
  }

  initializeDataTask = task({ drop: true }, async () => {
    await this.loadExternalSharingUrlTask.perform();
  });

  loadExternalSharingUrlTask = task({ restartable: true }, async () => {
    const url = urlBuilder.maps.getDraftMapSharingUrl(this.args.draft.id);
    try {
      const response = await this.ajax.request(url, {
        type: 'GET',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
      });
      const data = response as mapSharingItem[];
      if (data.length > 0) {
        this.externalSharingInfo = data[0];
        this.isSharingWithEveryone = true;
        this.shouldHideSensitiveInfo = !(
          data[0]?.config?.include?.includes(LIVE_MAP_SHARE_INCLUDE_FIELDS.SENSITIVE_INFO) ?? false
        );
      } else {
        this.externalSharingInfo = undefined;
      }
    } catch (err) {
      this.flashMessages.showFlash('error', err as string);
    }
  });

  createExternalSharingUrlTask = task({ restartable: true }, async () => {
    const url = urlBuilder.maps.getDraftMapSharingUrl(this.args.draft.id);
    const includeFields = [];
    if (!this.shouldHideSensitiveInfo) {
      includeFields.push(LIVE_MAP_SHARE_INCLUDE_FIELDS.SENSITIVE_INFO);
    }
    try {
      const response = await this.ajax.request(url, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
        data: JSON.stringify({
          data: {
            type: 'map-sharing',
            attributes: {
              config: {
                include: includeFields,
              },
            },
          },
        }),
      });
      const data = response as mapSharingItem;
      this.externalSharingInfo = data;
    } catch (err) {
      this.flashMessages.showFlash('error', err as string);
    }
  });

  deleteMapSharingUrlTask = task({ restartable: true }, async () => {
    const url = urlBuilder.maps.deleteMapSharingUrl(this.externalSharingInfo?.shareId as string);
    try {
      await this.ajax.request(url, {
        type: 'DELETE',
      });
    } catch (_e) {
      this.flashMessages.showFlash('error', 'Error deleting map sharing url');
    }
  });

  get shareStrategyOptions(): SelectOptionType[] {
    if (this.featureFlags.isEnabled('external-draft-sharing')) {
      return DRAFT_SHARE_OPTIONS;
    }
    return DRAFT_SHARE_OPTIONS.filter((option) => !option.gated);
  }

  get shareStrategy(): SelectOptionType {
    if (this.featureFlags.isEnabled('external-draft-sharing') && this.isSharingWithEveryone) {
      const shareWithEveryoneOption = this.shareStrategyOptions.find(
        (o) => o.value === DraftSharingStrategy.ANYONE_WITH_LINK,
      );
      if (shareWithEveryoneOption) {
        return shareWithEveryoneOption;
      }
    }
    if (!this.args.draft.sharingStrategy) {
      return this.shareStrategyOptions[0]!;
    }

    return this.shareStrategyOptions.find((o) => o.value === this.args.draft.sharingStrategy)!;
  }

  get draftName(): string {
    if (!this.args?.draft?.name?.trim()) {
      return 'Untitled draft';
    }

    return this.args.draft.name;
  }

  get draftUrl(): string {
    if (this.isSharingWithEveryone) {
      return this.externalSharingInfo?.url || '';
    }

    const draftAreaMapId = this.args.draft.belongsTo('draftAreaMap').id();
    return `${window.location.origin}/spaces/maps/drafts/${draftAreaMapId}`;
  }

  get shouldShowExpirationText(): boolean {
    return this.isSharingWithEveryone && this.externalSharingInfo?.expiresAt != null;
  }

  get expirationText(): string {
    const currentDate = new Date();
    const expirationDate = new Date(this.externalSharingInfo?.expiresAt as string);
    // +1 because the expiration date rounds down from current time
    const daysUntil = differenceInDays(expirationDate, currentDate) + 1;

    if (daysUntil <= 0) {
      return 'Link expired';
    }
    return `This link expires in ${pluralize(daysUntil, 'day')}`;
  }

  @action
  toggleHideSensitiveInfo(): void {
    this.isConfirmingSensitiveInfoChange = true;
    this.metrics.trackEvent('EXTERNAL_DRAFT_MAP_SHARE_LINK_PIN_TOGGLED', {
      showPii: !this.shouldHideSensitiveInfo,
    });

    this.shouldShowConfirmationModal = true;
  }

  @action
  trackCopyLink(): void {
    this.flashMessages.showAndHideFlash('success', 'Copied share URL');
    this.metrics.trackEvent('Copied draft share link', {
      draftId: this.args.draft.id,
      draftName: this.args.draft.name,
    });
  }

  @action
  async removeEmployeeAccess(employee: EmployeeModel): Promise<void> {
    try {
      await this.store.adapterFor('draft').updateRelationship(
        'draft',
        this.args.draft.id,
        'employees',
        this.args.draft.sharedEmployees.content.filter((e) => e.id !== employee.id),
      );

      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.draft.sharedEmployees.content.removeObjects(A([employee]));

      this.metrics.trackEvent('Removed employee draft access', {
        draftId: this.args.draft.id,
        draftName: this.args.draft.name,
        employeeId: employee.id,
        employeeName: employee.name,
      });

      this.flashMessages.showFlash('success', 'Access updated');
    } catch (err) {
      const error = parseErrorForDisplay(err) || 'Unknown error. Saving failed.';
      this.flashMessages.showFlash('error', error);
    }
  }

  @action
  async shareDraft(): Promise<void> {
    try {
      const employeeIds = this.selectedEmployees.map((e) => e.id);
      this.metrics.trackEvent('Shared draft with employee', {
        draftId: this.args.draft.id,
        draftName: this.args.draft.name,
        employeeIds: employeeIds,
      });

      if (!this.selectedEmployees.length) {
        return;
      }

      const allSharedEmployees = [...this.selectedEmployees, ...this.args.draft.sharedEmployees.content];

      await this.store
        .adapterFor('draft')
        .updateRelationship('draft', this.args.draft.id, 'employees', allSharedEmployees, {
          message: this.emailMessage,
          'send-email-notification': this.sendEmailNotification,
        });

      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.draft.sharedEmployees.content.pushObjects(A(this.selectedEmployees));

      this.selectedEmployees = [];
      this.emailMessage = '';
      this.sendEmailNotification = true;

      this.flashMessages.showFlash('success', 'Invite sent');
    } catch (err) {
      const error = parseErrorForDisplay(err) || 'Unknown error. Saving failed.';
      this.flashMessages.showFlash('error', error);
    }
  }

  @action
  exitShareMode(): void {
    this.selectedEmployees = [];
    this.sendEmailNotification = true;
    this.emailMessage = '';
  }

  @action
  onEmployeeChange(employees: EmployeeModel[]): void {
    this.selectedEmployees = employees;
  }

  @action
  async onShareStrategyChange(option: SelectOptionType): Promise<void> {
    try {
      if (option.value === DraftSharingStrategy.ANYONE_WITH_LINK) {
        this.isSharingWithEveryone = true;
        this.metrics.trackEvent('EXTERNAL_DRAFT_MAP_SHARE_LINK_CREATED');
        await this.createExternalSharingUrlTask.perform();
      } else {
        if (this.externalSharingInfo && this.shareStrategy.value === DraftSharingStrategy.ANYONE_WITH_LINK) {
          this.shouldShowConfirmationModal = true;
          this.pendingShareStrategy = option;
          return;
        }

        this.args.draft.sharingStrategy = option.value;
        await this.args.draft.save();
      }
    } catch (err) {
      const error = parseErrorForDisplay(err) || 'Unknown error. Saving failed.';
      this.flashMessages.showFlash('error', error);
    }
  }

  @action
  resetPendingState(): void {
    this.shouldShowConfirmationModal = false;
    this.isConfirmingSensitiveInfoChange = false;
    this.pendingShareStrategy = undefined;
  }

  @action
  async onConfirmShare(): Promise<void> {
    try {
      if (this.isConfirmingSensitiveInfoChange) {
        this.isConfirmingSensitiveInfoChange = false;
        this.shouldShowConfirmationModal = false;
        this.shouldHideSensitiveInfo = !this.shouldHideSensitiveInfo;
        await this.deleteMapSharingUrlTask.perform();
        await this.createExternalSharingUrlTask.perform();
        return;
      }

      await this.deleteMapSharingUrlTask.perform();
      this.args.draft.sharingStrategy = this.pendingShareStrategy?.value ?? DraftSharingStrategy.CUSTOM;
      await this.args.draft.save();
      this.isSharingWithEveryone = false;

      this.flashMessages.showFlash('success', 'Access updated');
      this.shouldShowConfirmationModal = false;
    } catch (err) {
      const error = parseErrorForDisplay(err) || 'Unknown error. Saving failed.';
      this.flashMessages.showFlash('error', error);
    }
  }
}
